import React from "react";
import { Link } from "react-router-dom";
import { Column } from "primereact/column";
import { ColumnGroup } from "primereact/columngroup";
import { DataTable } from "primereact/datatable";
import { Row } from "primereact/row";
import { CustomSortIcon } from "../../../../components/DatatableHelpers";
import { STUDENT_AVERAGE_MONTHLY_SCORES } from "../../student/progress/ProgressReport";
import { formatNumber, formatNumberOneDecimal } from "../../shared/reportUtil";
import { studentProgressLink } from "./ProgressReport";
import { WriterMonthlyProgress, MonthlyProgress } from "./MonthlyScoresFilteredByCourseViewModel";

export function MonthlyScoresTable(props: {
	className: string;
	courseId: number;
	writerData: WriterMonthlyProgress[];
	classData: MonthlyProgress;
}) {
	const hasData = props.writerData && props.writerData.length > 0;

	const studentNameTemplate = (rowData: WriterMonthlyProgress) => {
		return (
			<Link
				className="link"
				to={studentProgressLink(props.courseId, rowData.writerId)}
				state={{ section: STUDENT_AVERAGE_MONTHLY_SCORES }}>
				{rowData.writerName}
			</Link>
		);
	};

	const augScoreTemplate = (rowData: WriterMonthlyProgress) => {
		return formatNumber(rowData.augustAverageScore);
	};

	const sepScoreTemplate = (rowData: WriterMonthlyProgress) => {
		return formatNumber(rowData.septemberAverageScore);
	};

	const octScoreTemplate = (rowData: WriterMonthlyProgress) => {
		return formatNumber(rowData.octoberAverageScore);
	};

	const novScoreTemplate = (rowData: WriterMonthlyProgress) => {
		return formatNumber(rowData.novemberAverageScore);
	};

	const decScoreTemplate = (rowData: WriterMonthlyProgress) => {
		return formatNumber(rowData.decemberAverageScore);
	};

	const janScoreTemplate = (rowData: WriterMonthlyProgress) => {
		return formatNumber(rowData.januaryAverageScore);
	};

	const febScoreTemplate = (rowData: WriterMonthlyProgress) => {
		return formatNumber(rowData.februaryAverageScore);
	};

	const marScoreTemplate = (rowData: WriterMonthlyProgress) => {
		return formatNumber(rowData.marchAverageScore);
	};

	const aprScoreTemplate = (rowData: WriterMonthlyProgress) => {
		return formatNumber(rowData.aprilAverageScore);
	};

	const mayScoreTemplate = (rowData: WriterMonthlyProgress) => {
		return formatNumber(rowData.mayAverageScore);
	};

	const junScoreTemplate = (rowData: WriterMonthlyProgress) => {
		return formatNumber(rowData.juneAverageScore);
	};

	const julScoreTemplate = (rowData: WriterMonthlyProgress) => {
		return formatNumber(rowData.julyAverageScore);
	};

	const averageScoreTemplate = (rowData: WriterMonthlyProgress) => {
		return formatNumber(rowData.averageScore);
	};

	const footerGroup = (
		<ColumnGroup>
			<Row>
				<Column footer="Class" footerClassName="font-medium !text-right" />
				<Column
					footer={formatNumberOneDecimal(props.classData.augustAverageScore)}
					footerClassName="font-medium !px-2"
				/>
				<Column
					footer={formatNumberOneDecimal(props.classData.septemberAverageScore)}
					footerClassName="font-medium !px-2"
				/>
				<Column
					footer={formatNumberOneDecimal(props.classData.octoberAverageScore)}
					footerClassName="font-medium !px-2"
				/>
				<Column
					footer={formatNumberOneDecimal(props.classData.novemberAverageScore)}
					footerClassName="font-medium !px-2"
				/>
				<Column
					footer={formatNumberOneDecimal(props.classData.decemberAverageScore)}
					footerClassName="font-medium !px-2"
				/>
				<Column
					footer={formatNumberOneDecimal(props.classData.januaryAverageScore)}
					footerClassName="font-medium !px-2"
				/>
				<Column
					footer={formatNumberOneDecimal(props.classData.februaryAverageScore)}
					footerClassName="font-medium !px-2"
				/>
				<Column
					footer={formatNumberOneDecimal(props.classData.marchAverageScore)}
					footerClassName="font-medium !px-2"
				/>
				<Column
					footer={formatNumberOneDecimal(props.classData.aprilAverageScore)}
					footerClassName="font-medium !px-2"
				/>
				<Column
					footer={formatNumberOneDecimal(props.classData.mayAverageScore)}
					footerClassName="font-medium !px-2"
				/>
				<Column
					footer={formatNumberOneDecimal(props.classData.juneAverageScore)}
					footerClassName="font-medium !px-2"
				/>
				<Column
					footer={formatNumberOneDecimal(props.classData.julyAverageScore)}
					footerClassName="font-medium !px-2"
				/>
				<Column
					footer={formatNumberOneDecimal(props.classData.averageScore)}
					footerClassName="font-medium !px-2"
				/>
			</Row>
		</ColumnGroup>
	);

	return (
		<DataTable
			value={props.writerData}
			dataKey="writerId"
			loading={props.writerData == undefined}
			emptyMessage="No results found"
			stripedRows
			sortIcon={CustomSortIcon}
			className={`block tbl tbl-data tbl-datatable min-w-[40rem] cursor-default ${props.className}`}
			footerColumnGroup={hasData && footerGroup}>
			<Column
				header="Student"
				body={studentNameTemplate}
				sortable
				sortField="writerName"></Column>
			<Column
				header="AUG"
				body={augScoreTemplate}
				sortable
				sortField="augustAverageScore"
				headerClassName="!px-2"
				className="!px-2"></Column>
			<Column
				header="SEP"
				body={sepScoreTemplate}
				sortable
				sortField="septemberAverageScore"
				headerClassName="!px-2"
				className="!px-2"></Column>
			<Column
				header="OCT"
				body={octScoreTemplate}
				sortable
				sortField="octoberAverageScore"
				headerClassName="!px-2"
				className="!px-2"></Column>
			<Column
				header="NOV"
				body={novScoreTemplate}
				sortable
				sortField="novemberAverageScore"
				headerClassName="!px-2"
				className="!px-2"></Column>
			<Column
				header="DEC"
				body={decScoreTemplate}
				sortable
				sortField="decemberAverageScore"
				headerClassName="!px-2"
				className="!px-2"></Column>
			<Column
				header="JAN"
				body={janScoreTemplate}
				sortable
				sortField="januaryAverageScore"
				headerClassName="!px-2"
				className="!px-2"></Column>
			<Column
				header="FEB"
				body={febScoreTemplate}
				sortable
				sortField="februaryAverageScore"
				headerClassName="!px-2"
				className="!px-2"></Column>
			<Column
				header="MAR"
				body={marScoreTemplate}
				sortable
				sortField="marchAverageScore"
				headerClassName="!px-2"
				className="!px-2"></Column>
			<Column
				header="APR"
				body={aprScoreTemplate}
				sortable
				sortField="aprilAverageScore"
				headerClassName="!px-2"
				className="!px-2"></Column>
			<Column
				header="MAY"
				body={mayScoreTemplate}
				sortable
				sortField="mayAverageScore"
				headerClassName="!px-2"
				className="!px-2"></Column>
			<Column
				header="JUN"
				body={junScoreTemplate}
				sortable
				sortField="juneAverageScore"
				headerClassName="!px-2"
				className="!px-2"></Column>
			<Column
				header="JUL"
				body={julScoreTemplate}
				sortable
				sortField="julyAverageScore"
				headerClassName="!px-2"
				className="!px-2"></Column>
			<Column
				header="AVG"
				body={averageScoreTemplate}
				sortable
				sortField="averageScore"
				headerClassName="!px-2"
				className="!px-2"></Column>
		</DataTable>
	);
}
